import '../../common/EsCommon.css';
import './EsAccount.css';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { API_PASSWORD_RESET, API_PASSWORD_RESET_CHECK, TOKEN_PARAM, VIEW_FORGOT, VIEW_LOGIN } from '../../common/EsConstants';
import { esPost } from '../../common/EsFetch';
import { useEsStrings } from '../../common/EsStrings';

/**
 * "Choose a new password" -- the far end of the link in a reset mail.
 *
 * <h2>The token in the URL is the whole credential</h2>
 *
 * <p>Nobody reaching this page can log in; that is why they are here. So the
 * token out of {@code ?token=} stands in for a session, exactly as the
 * joiner's token does on {@link EsOptOut} -- and like that one it names
 * nothing and proves everything. The form never sends an address and there is
 * no box for one: a parameter naming whose password to change is the whole hole
 * this design exists to avoid.
 *
 * <p><b>A spent link draws no form either.</b> The token is asked about on
 * arrival (API_PASSWORD_RESET_CHECK, which spends nothing), so somebody opening
 * an old mail reads that it expired before they have typed a password twice. Only
 * a 400 means dead: a network blip or a 500 shows the form, because the server
 * still decides when the form is pressed.
 *
 * <p><b>No token, no form.</b> A truncated link -- and mail clients do truncate
 * them -- would otherwise draw two password boxes over a request that can only
 * be refused, so somebody would choose a password, press the button and be told
 * no by a server, about a link rather than about what they typed. The sentence
 * comes first instead, with the way back to asking for a new link.
 *
 * <h2>It ends on the login screen, and says nothing on the way</h2>
 *
 * <p>Tim's words on 2026-09-22: the reader "chooses a new password, then ends
 * on the login screen." So a clean answer navigates there and that is all --
 * {@code replace}, so the Back button does not return to a spent token and a
 * form that now refuses itself.
 *
 * <p>No {@code window.location.reload()}, which is the one thing worth saying
 * about what this does NOT do. EsLogin reloads because a session has just been
 * created and the whole app is still drawn logged out. Nothing here creates a
 * session: the reader still has to sign in with what they just chose, which is
 * the point of ending on that screen.
 */
export default function EsReset() {
    const t = useEsStrings();

    const [ params ] = useSearchParams();
    const navigate = useNavigate();

    const aToken = params.get( TOKEN_PARAM ) ?? '';

    const [ form, setForm ] = useState( { password: '', checkPassword: '' } );
    const [ error, setError ] = useState( '' );
    const [ saving, setSaving ] = useState( false );

    // 'checking' draws nothing under the heading, so a dead link never flashes a form.
    const [ link, setLink ] = useState( aToken ? 'checking' : 'live' );

    useEffect( () => {
        if ( !aToken ) return undefined;

        let aCancelled = false;

        esPost( API_PASSWORD_RESET_CHECK, { token: aToken } )
            .then( () => { if ( !aCancelled ) setLink( 'live' ); } )
            .catch( ( pFailure ) => {
                if ( !aCancelled ) setLink( pFailure.status === 400 ? 'dead' : 'live' );
            } );

        return () => { aCancelled = true; };
    }, [ aToken ] );

    const handleChange = ( pEvent ) => {
        const { name, value } = pEvent.target;
        setForm( { ...form, [ name ]: value } );
    };

    // The same three rules the register form applies, in the same order and the
    // same words. They are the browser's half of Cognito's policy and they are
    // deliberately not the whole of it -- the uppercase/lowercase/number/symbol
    // requirements are listed under the field and enforced where they are
    // known, which is Cognito. A refusal from there arrives as a msg on the
    // throw below and lands in this same box.
    function validateForm() {
        let aText = '';

        if ( !form.password ) aText += t( 'account-reset-password-required', 'Password is required.' ) + '\n';
        else if ( form.password.length < 10 ) aText += t( 'account-reset-password-short', '{count, plural, one {Password must be at least # character.} other {Password must be at least # characters.}}', { count: 10 } ) + '\n';

        if ( form.password && form.password !== form.checkPassword )
            aText += t( 'account-reset-password-mismatch', 'Password and Check Password do not match.' ) + '\n';

        setError( aText );

        return !aText;
    }

    const handleSubmit = async ( pEvent ) => {
        pEvent.preventDefault();
        if ( !validateForm() ) return;

        setSaving( true );

        try {
            // Built as one object rather than inline, because what esback needs
            // to identify the account is its call and not settled here: a token
            // it minted itself carries the address with it, where Cognito's own
            // confirmForgotPassword wants a username beside the code. If it
            // turns out to want the address too, it is one line in this object
            // and a field above -- not a reshaping of the screen.
            const aPayload = { token: aToken, password: form.password };

            await esPost( API_PASSWORD_RESET, aPayload );

            void navigate( VIEW_LOGIN, { replace: true } );
        }
        catch ( pFailure ) {
            // esback's own {"msg": ...} when there is one -- an expired token
            // and a password Cognito refused both arrive that way, and both
            // are sentences this reader can act on.
            //
            // NEVER pFailure.message as the fallback. esWrite's `written` does
            // a bare JSON.parse, so a whitelabel error page throws a
            // SyntaxError whose message is a complaint about an angle bracket;
            // the status is the only part of that worth putting on a screen.
            setError( pFailure.body?.msg
                || ( pFailure.status
                    ? t( 'account-reset-server-error', 'Sorry — the server could not set that password (error {status}).', { status: String( pFailure.status ) } )
                    : t( 'common-server-unreachable', 'Sorry — could not reach the server.' ) ) );
            setSaving( false );
        }
    };

    return (
        <section data-testid="es-reset-page" className="relative z-10 pb-4 pt-nav-room">
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">
                            <h3 data-testid="es-reset-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                                { t( 'account-reset-heading', 'Choose a new password' ) }
                            </h3>

                            { !aToken &&
                                <p data-testid="es-reset-no-token" className="accountNotice">
                                    { t( 'account-reset-invalid-line', 'Invalid reset link. Mail clients sometimes break a long link across two lines.  Try copy and pasting, or {ask} (with the same link).', { ask:
                                        <a href={ VIEW_FORGOT } data-testid="es-reset-forgot-link" className="text-accent hover:underline">
                                            { t( 'account-reset-invalid-ask', 'ask for a new one' ) }
                                        </a> } ) }
                                </p> }

                            { aToken && link === 'dead' &&
                                <p data-testid="es-reset-spent" className="accountNotice">
                                    { t( 'common-sentences', '{first} {second}', {
                                        first: t( 'account-reset-expired', 'That link has expired or has already been used.' ),
                                        second: <a href={ VIEW_FORGOT } data-testid="es-reset-spent-forgot-link" className="text-accent hover:underline">
                                            { t( 'account-reset-ask-again', 'Ask for a new one.' ) }
                                        </a> } ) }
                                </p> }

                            { aToken && link === 'live' &&
                                <>
                                    <p data-testid="es-reset-text" className="formSubHeader">
                                        { t( 'account-reset-blurb', 'Type it twice, and it\'s yours.' ) }
                                    </p>

                                    <form data-testid="es-reset-form" onSubmit={ handleSubmit }>
                                        <div className="mb-8">
                                            <label className="accountLabel" htmlFor="password">
                                                { t( 'account-reset-password', 'Password' ) }
                                            </label>
                                            <input autoComplete="new-password"
                                                onChange={ handleChange }
                                                type="password"
                                                id="password"
                                                name="password"
                                                value={ form.password }
                                                data-testid="es-reset-password"
                                                className="accountField" />
                                            <ul data-testid="es-reset-password-rule" className="mt-2 list-disc ps-5 text-xs text-aside">
                                                <li>{ t( 'account-register-rule-length', 'At least 10 characters' ) }</li>
                                                <li>{ t( 'account-register-rule-upper', 'An uppercase letter' ) }</li>
                                                <li>{ t( 'account-register-rule-lower', 'A lowercase letter' ) }</li>
                                                <li>{ t( 'account-register-rule-number', 'A number' ) }</li>
                                                <li>{ t( 'account-register-rule-symbol', 'A symbol' ) }</li>
                                            </ul>
                                        </div>
                                        <div className="mb-8">
                                            <label className="accountLabel" htmlFor="checkPassword">
                                                { t( 'account-reset-check-password', 'Check Password' ) }
                                            </label>
                                            <input autoComplete="new-password"
                                                onChange={ handleChange }
                                                type="password"
                                                id="checkPassword"
                                                name="checkPassword"
                                                value={ form.checkPassword }
                                                data-testid="es-reset-confirm"
                                                className="accountField" />
                                        </div>
                                        <div className="mb-6 pt-4">
                                            <button
                                                type="submit"
                                                disabled={ saving }
                                                data-testid="es-reset-submit"
                                                className="buttonSubmit flex w-full items-center justify-center">
                                                { t( 'account-reset-submit', 'Set password' ) }
                                            </button>
                                        </div>
                                    </form>
                                </> }

                            { error &&
                                <p data-testid="es-reset-error" className="formError"> { error } </p> }
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
